'use client'

import React, { useEffect } from 'react'
import { X } from 'lucide-react'

type OverlayProps = {
  open: boolean
  onClose: () => void
  children: React.ReactNode
  labelledBy?: string
}

export function ShoppingModalShell({
  open,
  onClose,
  children,
  widthClass = 'w-[534px]',
}: OverlayProps & { widthClass?: string }) {
  useLockBody(open)
  useEscapeToClose(open, onClose)
  if (!open) return null

  return (
    <div className="fixed inset-0 z-[1100] flex items-center justify-center p-4">
      <button
        type="button"
        aria-label="Close"
        className="absolute inset-0 bg-black/[0.35] backdrop-blur-[2px]"
        onClick={onClose}
      />
      <div
        className={`relative z-10 max-h-[90vh] overflow-y-auto rounded-2xl bg-white p-4 shadow-[0px_0px_25px_rgba(0,0,0,0.15)] ${widthClass}`}
      >
        {children}
      </div>
    </div>
  )
}

export function ShoppingDrawerShell({
  open,
  onClose,
  children,
}: OverlayProps) {
  useLockBody(open)
  useEscapeToClose(open, onClose)
  if (!open) return null

  return (
    <div className="fixed inset-0 z-[1100] flex justify-end">
      <button
        type="button"
        aria-label="Close"
        className="absolute inset-0 bg-black/[0.35] backdrop-blur-[2px]"
        onClick={onClose}
      />
      <aside className="relative z-10 flex h-full w-[420px] max-w-full flex-col gap-6 overflow-y-auto border-l border-[#E6E6E6] bg-white p-8 shadow-[-8px_0px_24px_rgba(0,0,0,0.10)]">
        {children}
      </aside>
    </div>
  )
}

export function ModalCloseButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Close"
      className="flex h-10 w-10 items-center justify-center rounded-full border border-[#E6E6E6] bg-[#F7F7F7] text-[#6D6D6D] hover:bg-[#EEEEEE]"
    >
      <X className="h-5 w-5" />
    </button>
  )
}

export function GreenPillButton({
  children,
  onClick,
  type = 'button',
  disabled,
  className = '',
}: {
  children: React.ReactNode
  onClick?: () => void
  type?: 'button' | 'submit'
  disabled?: boolean
  className?: string
}) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`flex h-[43px] items-center justify-center rounded-full bg-[#85C349] px-8 text-base font-medium text-white disabled:opacity-50 ${className}`}
    >
      {children}
    </button>
  )
}

export function TextCancelButton({
  onClick,
  children = 'Cancel',
}: {
  onClick: () => void
  children?: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="h-[30px] text-base font-normal text-[#202020]"
    >
      {children}
    </button>
  )
}

export function FieldLabel({ children }: { children: React.ReactNode }) {
  return <label className="text-base font-normal text-[#202020]">{children}</label>
}

export function TextField({
  value,
  onChange,
  placeholder,
  autoFocus,
}: {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  autoFocus?: boolean
}) {
  return (
    <input
      value={value}
      autoFocus={autoFocus}
      onChange={(event) => onChange(event.target.value)}
      placeholder={placeholder}
      className="h-[50px] w-full rounded-lg border border-[#CBD7DD] bg-white px-4 text-base font-medium text-[#202020] placeholder:font-normal placeholder:text-[#777777] outline-none focus:border-[#85C349]"
    />
  )
}

function useLockBody(open: boolean) {
  useEffect(() => {
    if (!open) return
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previous
    }
  }, [open])
}

function useEscapeToClose(open: boolean, onClose: () => void) {
  useEffect(() => {
    if (!open) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [open, onClose])
}
